import React from "react";
import { motion } from "framer-motion";
import { Flame, Dumbbell, CheckCircle2, Scale, TrendingUp } from "lucide-react";

const bars = [38, 52, 46, 64, 58, 72, 80];

export function PhoneFrame({ children, className = "" }) {
  return (
    <div className={`relative rounded-[2.6rem] border border-slate-700 bg-slate-950 p-2.5 shadow-2xl shadow-emerald-400/10 ${className}`}>
      <div className="absolute left-1/2 top-4 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-black" />
      <div className="h-full overflow-hidden rounded-[2.1rem] bg-[#060b16] px-4 pb-5 pt-12">{children}</div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400">
        <Icon className="h-3 w-3 text-emerald-400" /> {label}
      </div>
      <div className="mt-1 font-display text-xl font-bold text-white tabular-nums">{value}</div>
      <div className="text-[10px] text-emerald-400">{sub}</div>
    </div>
  );
}

function WorkoutRow({ name, meta, done }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-slate-900/80 px-3 py-2.5">
      <div className="flex items-center gap-2.5">
        <div className="grid h-7 w-7 place-items-center rounded-lg bg-emerald-400/10 text-emerald-400"><Dumbbell className="h-3.5 w-3.5" /></div>
        <div>
          <div className="text-xs font-semibold text-white">{name}</div>
          <div className="text-[10px] text-slate-400">{meta}</div>
        </div>
      </div>
      {done && <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
    </div>
  );
}

export function PhoneMockup({ lang }) {
  const id = lang === "id";
  return (
    <motion.div initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
      className="relative" data-testid="hero-phone-mockup">
      <div aria-hidden className="absolute -inset-10 -z-10 rounded-full bg-emerald-400/20 blur-3xl" />
      <PhoneFrame className="landing-float w-[280px] sm:w-[310px]">
        <div className="text-[11px] text-slate-400">{id ? "Selamat pagi" : "Good morning"}</div>
        <div className="font-display text-lg font-bold text-white">{id ? "Siap latihan hari ini?" : "Ready to train today?"}</div>

        <div className="mt-4 flex items-center justify-between rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-3">
          <div className="flex items-center gap-2.5">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-400 text-slate-950"><Flame className="h-5 w-5" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-300">Streak</div>
              <div className="font-display text-base font-bold text-white">12 {id ? "hari" : "days"}</div>
            </div>
          </div>
          <span className="rounded-full bg-emerald-400 px-2.5 py-1 text-[10px] font-bold text-slate-950">Check-in</span>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <StatTile icon={Scale} label="BMI" value="22.4" sub="Normal" />
          <StatTile icon={TrendingUp} label={id ? "Berat" : "Weight"} value="68.2" sub={id ? "-1.4 kg bulan ini" : "-1.4 kg this month"} />
        </div>

        <div className="mt-3 rounded-2xl border border-slate-800 bg-slate-900/80 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">{id ? "Volume mingguan" : "Weekly volume"}</div>
          <div className="mt-2 flex h-14 items-end gap-1.5">
            {bars.map((h, i) => (
              <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: 0.6 + i * 0.07, duration: 0.5 }}
                className={`flex-1 rounded-sm ${i === bars.length - 1 ? "bg-emerald-400" : "bg-emerald-400/30"}`} />
            ))}
          </div>
        </div>

        <div className="mt-3 space-y-2">
          <WorkoutRow name="Bench Press" meta={id ? "4 set · 60 kg" : "4 sets · 60 kg"} done />
          <WorkoutRow name="Barbell Squat" meta={id ? "4 set · 80 kg" : "4 sets · 80 kg"} />
        </div>
      </PhoneFrame>
    </motion.div>
  );
}
